import React, {createContext, useContext, useState} from 'react';
import {useIntl} from 'react-intl';
import Util from '../../../../shared/util/util';
import {ESTIMATION_UNIT, EstimationUnit} from '../../../../../constants';
import * as tracking from '../../../../../tracking';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {idFromGlobalId} from '../../../../shared/util/GlobalIdUtil';
import {TotalsContextData} from './totals-header-box-tiles/TotalsTypes';
import {noop} from 'lodash';

const TotalsContext = createContext<TotalsContextData>({
	totalsExpanded: false,
	toggleTotalsExpanded: noop,
	projectData: {},
	settings: {},
	totalsData: {
		startDateString: '',
		endDateString: '',
		progress: 0,
		plannedRevenue: 0,
		actualRevenue: 0,
		actualRevenueForProject: 0,
		totalPriceAtCompletion: 0,
		totalPriceAtCompletionForTasks: 0,
		totalPriceAtCompletionForProject: 0,
		estimate: 0,
		estimateOnTasks: 0,
		timeEntries: 0,
		billableTimeEntries: 0,
		timeEntriesOnTasks: 0,
		timeEntriesOnProject: 0,
		remaining: 0,
		remainingOnTasks: 0,
		difference: 0,
		varianceOnTasks: 0,
		totalAvailability: 0,
		remainingAvailability: 0,
		baselineTargetHours: 0,
		baselineTargetPrice: 0,
		allTotalTimeAndExpensesAtCompletion: 0,
		projectedTotalCost: 0,
		projectedTotalRevenue: 0,
		projectedTotalProfit: 0,
		projectedTotalMargin: 0,
		projectedTotalHours: 0,
		actualHours: 0,
		actualBillableHours: 0,
		actualNonBillableHours: 0,
		projectedBillableValueOfService: 0,
		baselineHours: 0,
		isEstimatedInHours: false,
		allocatedHours: 0,
		estimatedHours: 0,
		remainingHours: 0,
		baselineRevenue: 0,
		plannedCost: 0,
		baselineCost: 0,
		actualCost: 0,
		projectedBillableHours: 0,
		projectedNonBillableHours: 0,
		plannedProfit: 0,
		baselineProfit: 0,
		plannedMargin: 0,
		estimatedPoints: 0,
		completedPoints: 0,
		remainingPoints: 0,
		remainingCost: 0,
		baselineMargin: 0,
		billableActualValueOfService: 0,
		actualValueOfService: 0,
		baselineValueOfService: 0,
		projectedTotalValueOfService: 0,
		remainingBudget: 0,
		overUnderServicing: 0,
		percentageActualBillableHours: 0,
		percentageProjectedTotalBillableHours: 0,
		taskRegisteredMinutes: 0,
		projectRegisteredMinutes: 0,
	},
});

type RolledUpData = {
	projectProgress: number;
	totalPriceAtCompletion: number;
	estimationUnit: EstimationUnit;
	estimate: number;
	timeRegistered: number;
	remaining: number;
	totalAvailability: number;
	remainingAvailability: number;
};

type ProjectData = {
	projectStartYear: unknown;
	projectStartMonth: unknown;
	projectStartDay: unknown;
	projectEndYear: unknown;
	projectEndMonth: unknown;
	projectEndDay: unknown;
	manualProgressEnabled: unknown;
	progress: number;
	rolledUpData: RolledUpData;
	projectRevenueNoTask: number;
	projectTotalPriceAtCompletionNoTask: number;
	minutesPerEstimationPoint: number;
	projectTimeEntries: number;
	projectBillableTimeEntries: number;
	projectTimeRegisteredNoTask: number;
	manualProgressOnProjectEnabled: boolean;
	manualProgressOnPhasesEnabled: boolean;
	projectDifferenceEstimate: number;
	projectAllTotalTimeAndExpensesAtCompletion: number;
	budget: number;
};

type BaselineFinancialNumbers = {
	phaseId: number;
	baselineMinutes: number;
	baselineTimeAndExpenses: number;
};

type TotalFinancialNumbers = {
	totalCostAtCompletion: number;
	totalRevenueRecognition: number;
	totalRevenueProfitAtCompletion: number;
	totalRevenueMarginAtCompletion: number;
	forecastMinutes: number;
	registeredMinutes: number;
	taskRegisteredMinutes: number;
	projectRegisteredMinutes: number;
	billableActualMinutes: number;
	nonBillableActualMinutes: number;
	billableTotalTimeAndExpensesAtCompletion: number;
	baselineMinutes: number;
	projectedTotalMinutes: number;
	allocationMinutes: number;
	scopeApprovedMinutes: number;
	remainingMinutes: number;
	baselineRevenue: number;
	plannedCost: number;
	baselineCost: number;
	actualCost: number;
	projectedNonBillableMinutes: number;
	projectedBillableMinutes: number;
	plannedRevenue: number;
	plannedProfit: number;
	baselineProfit: number;
	plannedMargin: number;
	remainingCost: number;
	actualRevenue: number;
	baselineMargin: number;
	billableActualTimeAndExpenses: number;
	nonBillableActualTimeAndExpenses: number;
	baselineTimeAndExpenses: number;
	allTotalTimeAndExpensesAtCompletion: number;
};

type ProjectScopingTotalsProviderProps = {
	projectData: ProjectData;
	settings: Record<string, unknown>;
	baselineFinancialNumbers: BaselineFinancialNumbers[];
	totalFinancialNumbers: TotalFinancialNumbers;
	phases: {id: string}[];
};

export const ProjectScopingTotalsProvider: React.FC<ProjectScopingTotalsProviderProps> = ({
	children,
	projectData,
	settings,
	baselineFinancialNumbers,
	totalFinancialNumbers,
	phases,
}) => {
	const intl = useIntl();

	const TOTALS_EXPANDED_KEY = 'project-scoping-totals-expanded';

	const [totalsExpanded, setTotalsExpanded] = useState(JSON.parse(Util.localStorageGetItem(TOTALS_EXPANDED_KEY) || true));

	const startDate = Util.CreateNonUtcMomentDate(
		projectData.projectStartYear,
		projectData.projectStartMonth,
		projectData.projectStartDay
	);
	const endDate = Util.CreateNonUtcMomentDate(
		projectData.projectEndYear,
		projectData.projectEndMonth,
		projectData.projectEndDay
	);

	const startDateString = startDate
		? `${intl.formatDate(startDate, {day: 'numeric'})} ${intl.formatDate(startDate, {month: 'short'})}, ${intl.formatDate(
				startDate,
				{year: 'numeric'}
		  )}`
		: intl.formatMessage({id: 'project_overview.no_start_date'});
	const endDateString = endDate
		? `${intl.formatDate(endDate, {day: 'numeric'})} ${intl.formatDate(endDate, {month: 'short'})}, ${intl.formatDate(
				endDate,
				{year: 'numeric'}
		  )}`
		: intl.formatMessage({id: 'project_overview.no_end_date'});

	// All references to fields in projectData.rolledUpData should eventually be replaced by fields calculated by the finance service
	const progress = projectData.manualProgressEnabled
		? projectData.progress / 100.0
		: projectData.rolledUpData.projectProgress
		? projectData.rolledUpData.projectProgress / 100.0
		: 0;

	const actualRevenueForProject = projectData.projectRevenueNoTask
		? Util.getFormattedCurrencyValue(
				settings.currencySymbol,
				intl.formatNumber(projectData.projectRevenueNoTask, {format: 'always_two_decimal'})
		  )
		: '-';

	const totalPriceAtCompletion = projectData.rolledUpData.totalPriceAtCompletion;
	const totalPriceAtCompletionForTasks = projectData.rolledUpData.totalPriceAtCompletion
		? Util.getFormattedCurrencyValue(
				settings.currencySymbol,
				intl.formatNumber(projectData.rolledUpData.totalPriceAtCompletion, {format: 'always_two_decimal'})
		  )
		: '-';
	const totalPriceAtCompletionForProject = projectData.projectTotalPriceAtCompletionNoTask
		? Util.getFormattedCurrencyValue(
				settings.currencySymbol,
				intl.formatNumber(projectData.projectTotalPriceAtCompletionNoTask, {format: 'always_two_decimal'})
		  )
		: '-';

	const isEstimatedInHours = projectData.rolledUpData.estimationUnit === ESTIMATION_UNIT.HOURS;

	const estimate = projectData.rolledUpData.estimate
		? isEstimatedInHours
			? projectData.rolledUpData.estimate / 60
			: (projectData.rolledUpData.estimate / 60) * projectData.minutesPerEstimationPoint
		: 0;

	const estimateOnTasksFormatted = projectData.rolledUpData.estimate
		? isEstimatedInHours
			? Util.convertMinutesToFullHour(projectData.rolledUpData.estimate, intl)
			: Util.convertMinutesToFullHour(projectData.rolledUpData.estimate * projectData.minutesPerEstimationPoint, intl)
		: '-';

	const timeEntries = projectData.projectTimeEntries ? projectData.projectTimeEntries / 60 : 0;

	const billableTimeEntries = projectData.projectBillableTimeEntries ? projectData.projectBillableTimeEntries / 60 : 0;

	const timeEntriesOnTasks = projectData.rolledUpData.timeRegistered
		? Util.convertMinutesToFullHour(projectData.rolledUpData.timeRegistered, intl)
		: '-';

	const timeEntriesOnProject = projectData.projectTimeRegisteredNoTask
		? Util.convertMinutesToFullHour(projectData.projectTimeRegisteredNoTask, intl)
		: '-';

	const remaining =
		projectData.manualProgressOnProjectEnabled || projectData.manualProgressOnPhasesEnabled
			? estimate - estimate * progress
			: projectData.rolledUpData.remaining
			? isEstimatedInHours
				? projectData.rolledUpData.remaining / 60
				: (projectData.rolledUpData.remaining / 60) * projectData.minutesPerEstimationPoint
			: 0;

	const remainingOnTasksFormatted =
		projectData.manualProgressOnProjectEnabled || projectData.manualProgressOnPhasesEnabled
			? isEstimatedInHours
				? Util.convertMinutesToFullHour(estimate - estimate * progress, intl)
				: Util.convertMinutesToFullHour((estimate - estimate * progress) * 60, intl)
			: projectData.rolledUpData.remaining
			? isEstimatedInHours
				? Util.convertMinutesToFullHour(projectData.rolledUpData.remaining, intl)
				: Util.convertMinutesToFullHour(
						projectData.rolledUpData.remaining * projectData.minutesPerEstimationPoint,
						intl
				  )
			: '-';

	const difference = projectData.projectDifferenceEstimate ? projectData.projectDifferenceEstimate / 60 : 0;

	const varianceOnTasksFormatted = projectData.projectDifferenceEstimate
		? Util.convertMinutesToFullHour(projectData.projectDifferenceEstimate, intl)
		: '-';

	const totalAvailability = projectData.rolledUpData.totalAvailability ? projectData.rolledUpData.totalAvailability / 60 : 0;
	const remainingAvailability = projectData.rolledUpData.remainingAvailability
		? projectData.rolledUpData.remainingAvailability / 60
		: 0;

	const phaseIds = new Set(phases.map(phase => idFromGlobalId(phase.id)));
	const baselineFinancialNumbersPhaseFiltered = baselineFinancialNumbers.filter(financialNumbers =>
		phaseIds.has(financialNumbers.phaseId)
	);
	const baselineTargetMinutes = baselineFinancialNumbersPhaseFiltered.reduce(
		(total, financialNumbers) => total + financialNumbers.baselineMinutes,
		0
	);
	const baselineTargetHours = baselineTargetMinutes ? baselineTargetMinutes / 60 : 0;
	const baselineTargetPrice = baselineFinancialNumbersPhaseFiltered.reduce(
		(total, financialNumbers) => total + financialNumbers.baselineTimeAndExpenses,
		0
	);
	const allTotalTimeAndExpensesAtCompletion = projectData.projectAllTotalTimeAndExpensesAtCompletion;

	const projectedTotalCost = totalFinancialNumbers.totalCostAtCompletion;
	const projectedTotalRevenue = totalFinancialNumbers.totalRevenueRecognition;
	const projectedTotalProfit = totalFinancialNumbers.totalRevenueProfitAtCompletion;
	const projectedTotalMargin = totalFinancialNumbers.totalRevenueMarginAtCompletion;
	const projectedTotalHours = totalFinancialNumbers.projectedTotalMinutes / 60;
	const actualHours = totalFinancialNumbers.registeredMinutes / 60;
	const actualBillableHours = totalFinancialNumbers.billableActualMinutes / 60;
	const actualNonBillableHours = totalFinancialNumbers.nonBillableActualMinutes / 60;
	const projectedBillableValueOfService = totalFinancialNumbers.billableTotalTimeAndExpensesAtCompletion;
	const baselineHours = totalFinancialNumbers.baselineMinutes / 60;
	const allocatedHours = totalFinancialNumbers.allocationMinutes / 60;
	const estimatedHours = totalFinancialNumbers.scopeApprovedMinutes / 60;
	const remainingHours = totalFinancialNumbers.remainingMinutes / 60;
	const baselineRevenue = totalFinancialNumbers.baselineRevenue;
	const plannedCost = totalFinancialNumbers.plannedCost;
	const baselineCost = totalFinancialNumbers.baselineCost;
	const actualCost = totalFinancialNumbers.actualCost;
	const projectedNonBillableHours = totalFinancialNumbers.projectedNonBillableMinutes / 60;
	const projectedBillableHours = totalFinancialNumbers.projectedBillableMinutes / 60;
	const plannedRevenue = totalFinancialNumbers.plannedRevenue;
	const plannedProfit = totalFinancialNumbers.plannedProfit;
	const baselineProfit = totalFinancialNumbers.baselineProfit;
	const plannedMargin = totalFinancialNumbers.plannedMargin;
	const estimatedPoints =
		projectData.minutesPerEstimationPoint > 0
			? totalFinancialNumbers.scopeApprovedMinutes / projectData.minutesPerEstimationPoint
			: 0;
	const completedPoints =
		projectData.minutesPerEstimationPoint > 0
			? totalFinancialNumbers.registeredMinutes / projectData.minutesPerEstimationPoint
			: 0;
	const remainingPoints =
		projectData.minutesPerEstimationPoint > 0
			? totalFinancialNumbers.remainingMinutes / projectData.minutesPerEstimationPoint
			: 0;
	const remainingCost = totalFinancialNumbers.remainingCost;
	const actualRevenue = totalFinancialNumbers.actualRevenue;
	const baselineMargin = totalFinancialNumbers.baselineMargin;
	const billableActualValueOfService = totalFinancialNumbers.billableActualTimeAndExpenses;
	const actualValueOfService = billableActualValueOfService + totalFinancialNumbers.nonBillableActualTimeAndExpenses;
	const baselineValueOfService = totalFinancialNumbers.baselineTimeAndExpenses;
	const projectedTotalValueOfService = totalFinancialNumbers.allTotalTimeAndExpensesAtCompletion;
	const remainingBudget = projectData.budget - billableActualValueOfService;
	const overUnderServicing = projectData.budget - actualValueOfService;
	const percentageActualBillableHours = actualHours > 0 ? actualBillableHours / actualHours : 0;
	const percentageProjectedTotalBillableHours = projectedTotalHours > 0 ? projectedBillableHours / projectedTotalHours : 0;
	const taskRegisteredMinutes = totalFinancialNumbers.taskRegisteredMinutes;
	const projectRegisteredMinutes = totalFinancialNumbers.projectRegisteredMinutes;

	const toggleTotalsExpanded = () => {
		setTotalsExpanded(value => {
			const newValue = !value;
			Util.localStorageSetItem(TOTALS_EXPANDED_KEY, newValue);
			tracking.trackElementClicked('Expand scoping totals', {enabled: newValue});
			trackEvent('Scoping Totals', newValue ? 'Expanded' : 'Collapsed');

			return newValue;
		});
	};

	return (
		<TotalsContext.Provider
			value={{
				totalsExpanded,
				toggleTotalsExpanded,
				projectData, // The original project data
				settings, // The settings
				totalsData: {
					startDateString,
					endDateString,
					progress,
					plannedRevenue,
					actualRevenue,
					actualRevenueForProject,
					totalPriceAtCompletion,
					totalPriceAtCompletionForTasks,
					totalPriceAtCompletionForProject,
					estimate,
					estimateOnTasks: estimateOnTasksFormatted,
					timeEntries,
					billableTimeEntries,
					timeEntriesOnTasks,
					timeEntriesOnProject,
					remaining,
					remainingOnTasks: remainingOnTasksFormatted,
					difference,
					varianceOnTasks: varianceOnTasksFormatted,
					totalAvailability,
					remainingAvailability,
					baselineTargetHours,
					baselineTargetPrice,
					allTotalTimeAndExpensesAtCompletion,
					projectedTotalCost,
					projectedTotalRevenue,
					projectedTotalProfit,
					projectedTotalMargin,
					projectedTotalHours,
					actualHours,
					actualBillableHours,
					actualNonBillableHours,
					projectedBillableValueOfService,
					baselineHours,
					allocatedHours,
					isEstimatedInHours,
					estimatedHours,
					remainingHours,
					baselineRevenue,
					plannedCost,
					baselineCost,
					actualCost,
					projectedBillableHours,
					projectedNonBillableHours,
					plannedProfit,
					baselineProfit,
					plannedMargin,
					estimatedPoints,
					completedPoints,
					remainingPoints,
					remainingCost,
					baselineMargin,
					billableActualValueOfService,
					actualValueOfService,
					baselineValueOfService,
					projectedTotalValueOfService,
					remainingBudget,
					overUnderServicing,
					percentageActualBillableHours,
					percentageProjectedTotalBillableHours,
					taskRegisteredMinutes,
					projectRegisteredMinutes,
				},
			}}
		>
			{children}
		</TotalsContext.Provider>
	);
};

export const useProjectTotals = (): TotalsContextData => useContext(TotalsContext);
